<template>
  <div class="combo-page-root">
    <AppNavbar />

    <header class="combo-hero-banner">
      <div class="hero-glow"></div>
      <div class="banner-inner-container">
        <nav class="breadcrumb-nav">
          <a href="/" class="crumb-link">Home</a>
          <span class="separator">/</span>
          <span class="current-leaf">Super Promo</span>
        </nav>

        <h1 class="page-title-main">Super Promo</h1>
        <div class="title-underline"></div>
        <p class="page-subtitle">
          Curated pairings, bundled at a special price.
        </p>
      </div>
    </header>

    <main class="combo-content">
      <div class="combo-cards-container">
        <article
          v-for="combo in comboProducts"
          :key="combo.id"
          class="combo-card"
        >
          <div class="combo-summary">
            <!-- IMAGE CAROUSEL -->
            <div class="combo-carousel">
              <div class="carousel-square">
                <img
                  v-if="getActiveImage(combo)"
                  :src="getActiveImage(combo)"
                  :alt="combo.name"
                  class="carousel-image"
                  loading="lazy"
                />
                <div v-else class="combo-image-fallback">
                  <span>No Image Available</span>
                </div>

                <button
                  v-if="combo.images.length > 1"
                  type="button"
                  class="carousel-arrow prev"
                  aria-label="Previous image"
                  @click.stop="stepCarousel(combo, -1)"
                >
                  <i class="ri-arrow-left-s-line"></i>
                </button>
                <button
                  v-if="combo.images.length > 1"
                  type="button"
                  class="carousel-arrow next"
                  aria-label="Next image"
                  @click.stop="stepCarousel(combo, 1)"
                >
                  <i class="ri-arrow-right-s-line"></i>
                </button>
              </div>

              <div v-if="combo.images.length > 1" class="carousel-dots">
                <button
                  v-for="(img, i) in combo.images"
                  :key="i"
                  type="button"
                  class="carousel-dot"
                  :class="{ active: getActiveIndex(combo) === i }"
                  :aria-label="`View image ${i + 1}`"
                  @click.stop="setCarouselIndex(combo, i)"
                ></button>
              </div>
            </div>

            <!-- INFO -->
            <div class="combo-info-col">
              <span class="combo-eyebrow">Super Promo</span>
              <h3 class="combo-name">{{ combo.name }}</h3>

              <div class="included-names-row">
                <span
                  v-for="product in combo.products"
                  :key="product.productId"
                  class="included-name-chip"
                >
                  {{ product.name }}
                </span>
              </div>

              <div class="combo-price-row">
                <span class="price-net">{{
                  formatRs(getPriceInfo(combo).price)
                }}</span>
                <span v-if="getPriceInfo(combo).showMrp" class="price-mrp">{{
                  formatRs(getPriceInfo(combo).mrp)
                }}</span>
              </div>

              <div class="combo-facts-line">
                <span v-if="getPriceInfo(combo).showCv">
                  CV {{ combo.cv }}
                </span>
                <span
                  v-if="
                    getPriceInfo(combo).showCv &&
                    Number(combo.shippingAndHandling) > 0
                  "
                  class="facts-dot"
                  >&middot;</span
                >
                <span v-if="Number(combo.shippingAndHandling) > 0">
                  Shipping {{ formatRs(combo.shippingAndHandling) }}
                </span>
              </div>
            </div>

            <!-- ACTION -->
            <div class="action-btn-wrapper">
              <button
                type="button"
                class="brand-movement-add-btn"
                :class="{ adding: addingComboIds[combo.id] }"
                :disabled="!!addingComboIds[combo.id]"
                @click.stop="handleAddComboToCart(combo)"
              >
                {{
                  addingComboIds[combo.id] ? "Adding..." : "Add Combo to Cart"
                }}
              </button>

              <transition name="toast-fade">
                <div v-if="toastVisibleIds[combo.id]" class="combo-toast">
                  ✓ Added to cart
                </div>
              </transition>
            </div>
          </div>

          <!-- INCLUDED PRODUCTS — always visible, editorial layout, no accordion -->
          <div class="included-section">
            <div class="included-eyebrow">
              <i class="ri-gift-line"></i>
              <span>What's Inside</span>
            </div>

            <div class="included-list">
              <div
                v-for="product in combo.products"
                :key="product.productId"
                class="included-item"
              >
                <div class="included-item-head">
                  <h4 class="included-item-name">{{ product.name }}</h4>
                  <span class="included-item-brand">{{ product.brand }}</span>
                </div>
                <span class="included-item-qty">{{
                  product.netQuantityLabel
                }}</span>

                <!-- Story preview is always shown (clamped to 2 lines);
                     Read more reveals the full story PLUS the
                     manufacturer facts and address together. -->
                <div class="included-item-expand-block">
                  <p
                    v-if="product.story"
                    class="included-item-story"
                    :class="{ clamped: !isStoryExpanded(combo, product) }"
                  >
                    {{ product.story }}
                  </p>

                  <div
                    v-show="isStoryExpanded(combo, product)"
                    class="included-item-extra"
                  >
                    <div class="included-item-facts">
                      <span v-if="product.countryOfManufacture">
                        <i class="ri-map-pin-line"></i>
                        Made in {{ product.countryOfManufacture }}
                      </span>
                      <span v-if="product.manufacturedBy">
                        <i class="ri-building-2-line"></i>
                        {{ product.manufacturedBy }}
                      </span>
                      <span v-if="product.marketedBy">
                        <i class="ri-megaphone-line"></i>
                        Marketed by {{ product.marketedBy }}
                      </span>
                    </div>

                    <p
                      v-if="product.manufacturerAddress"
                      class="included-item-address"
                    >
                      <i class="ri-map-2-line"></i>
                      {{ product.manufacturerAddress }}
                    </p>
                  </div>

                  <button
                    type="button"
                    class="story-toggle"
                    :aria-expanded="isStoryExpanded(combo, product)"
                    @click.stop="toggleStory(combo, product)"
                  >
                    <span>{{
                      isStoryExpanded(combo, product)
                        ? "Read less"
                        : "Read more"
                    }}</span>
                    <i
                      :class="
                        isStoryExpanded(combo, product)
                          ? 'ri-arrow-up-s-line'
                          : 'ri-arrow-down-s-line'
                      "
                    ></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </main>

    <Footer />
  </div>
</template>

<script setup>
import { reactive } from "vue";
import AppNavbar from "../AppNavbar.vue";
import Footer from "../FooterView.vue";
import {
  COMBO_PRODUCTS,
  getUserRoleToken,
  getUserFirstOrderCompleted,
  getComboPriceInfo,
  getCouponInfo,
  formatRs,
} from "@/data/comboProducts.js";
import { useCartStore } from "../../stores/useCartStore.js";

const comboProducts = COMBO_PRODUCTS;
const cartStore = useCartStore();

// combo.id -> current carousel index
const carouselIndex = reactive({});

// storyKey -> true when the user has expanded that product's block
// (story + manufacturer facts + address all reveal together)
const expandedStories = reactive({});

// combo.id -> true while that combo's "Add Combo to Cart" request is
// in flight (disables the button so it can't be double-clicked)
const addingComboIds = reactive({});

// combo.id -> true while the "✓ Added to cart" toast for that combo is
// visible. Scoped per combo (not a single global toast) so adding two
// different combos back-to-back doesn't hide one card's confirmation
// with another's.
const toastVisibleIds = reactive({});
const toastTimers = {};

function getPriceInfo(combo) {
  return getComboPriceInfo(
    combo,
    getUserRoleToken(),
    getUserFirstOrderCompleted(),
  );
}

function getActiveIndex(combo) {
  return carouselIndex[combo.id] || 0;
}

function getActiveImage(combo) {
  return combo.images[getActiveIndex(combo)];
}

function setCarouselIndex(combo, index) {
  carouselIndex[combo.id] = index;
}

function stepCarousel(combo, delta) {
  const total = combo.images.length;
  const next = (getActiveIndex(combo) + delta + total) % total;
  carouselIndex[combo.id] = next;
}

/* ---------- READ MORE / READ LESS ---------- */

// The same productId can appear in more than one combo, so scope the key.
function storyKey(combo, product) {
  return `${combo.id}::${product.productId}`;
}

function isStoryExpanded(combo, product) {
  return !!expandedStories[storyKey(combo, product)];
}

function toggleStory(combo, product) {
  const key = storyKey(combo, product);
  expandedStories[key] = !expandedStories[key];
}

/* ---------- ADD TO CART (toast confirmation, no qty adjuster) ---------- */

async function handleAddComboToCart(combo) {
  if (addingComboIds[combo.id]) return;

  const products = combo.products.map((product) => ({
    productId: product.productId,
    sku: product.sku,
    name: product.name,
    netPrice: product.netPrice,
    coinValue: product.cv,
    shippingCharge: product.shippingCharge,
    image: product.image,
  }));

  const roleToken = getUserRoleToken();
  const firstOrderCompleted = getUserFirstOrderCompleted();
  const loyaltyCards = [
    {
      id: getCouponInfo(combo, roleToken, firstOrderCompleted),
      type: "Coupon",
    },
  ];

  addingComboIds[combo.id] = true;

  try {
    await cartStore.addComboToCart(products, loyaltyCards);

    // Show the confirmation toast for this combo, resetting the
    // hide-timer on repeat clicks instead of letting timers stack.
    toastVisibleIds[combo.id] = true;
    clearTimeout(toastTimers[combo.id]);
    toastTimers[combo.id] = setTimeout(() => {
      toastVisibleIds[combo.id] = false;
    }, 2800);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to add combo to cart:", combo.id, error);
  } finally {
    addingComboIds[combo.id] = false;
  }
}
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap");
@import url("https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css");

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.combo-page-root {
  --brand-blue: #003da5;
  --brand-gold: #ffc72c;
  --ink: #0f2544;
  --ink-soft: #57627a;
  --ink-faint: #8892a6;
  --rule: rgba(15, 37, 68, 0.09);
  --paper: #f7f8fb;
  --card: #ffffff;
  background: var(--paper);
  color: var(--ink-soft);
  min-height: 100vh;
  font-family: "Outfit", sans-serif;
}

/* HERO */
.combo-hero-banner {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: linear-gradient(160deg, var(--brand-blue) 0%, #002a73 100%);
  padding: 20px 0;
}
.hero-glow {
  position: absolute;
  width: 420px;
  height: 420px;
  border-radius: 50%;
  background: rgba(255, 199, 44, 0.12);
  filter: blur(110px);
  top: -160px;
  right: -80px;
}
.banner-inner-container {
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 2;
}
.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 22px;
}
.crumb-link {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
}
.crumb-link:hover {
  color: #fff;
}
.breadcrumb-nav .separator {
  color: rgba(255, 255, 255, 0.35);
}
.breadcrumb-nav .current-leaf {
  color: var(--brand-gold);
  font-weight: 600;
}
.page-title-main {
  font-size: 42px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: -0.5px;
}
.title-underline {
  width: 56px;
  height: 3px;
  background: var(--brand-gold);
  border-radius: 2px;
  margin: 16px 0 14px;
}
.page-subtitle {
  font-size: 15.5px;
  color: rgba(255, 255, 255, 0.78);
}

/* LAYOUT */
.combo-content {
  max-width: 1180px;
  margin: 0 auto;
  padding: 30px 24px 50px;
}
.combo-cards-container {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.combo-card {
  background: var(--card);
  border: 1px solid var(--rule);
  border-radius: 18px;
  overflow: hidden;
  transition: box-shadow 0.25s ease, transform 0.25s ease;
}
.combo-card:hover {
  box-shadow: 0 18px 34px rgba(15, 37, 68, 0.07);
}

/* SUMMARY ROW */
.combo-summary {
  display: grid;
  grid-template-columns: 210px 1fr auto;
  gap: 30px;
  align-items: center;
  padding: 30px 32px;
}

.combo-carousel {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.carousel-square {
  position: relative;
  width: 210px;
  height: 210px;
  border-radius: 14px;
  overflow: hidden;
  background: #fafafa;
  border: 1px solid var(--rule);
}
.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.combo-image-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-faint);
  font-size: 12px;
  font-weight: 600;
}
.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 37, 68, 0.45);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 17px;
  transition: background 0.2s ease;
}
.carousel-arrow:hover {
  background: var(--brand-blue);
}
.carousel-arrow.prev {
  left: 8px;
}
.carousel-arrow.next {
  right: 8px;
}
.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
}
.carousel-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 37, 68, 0.18);
  cursor: pointer;
  padding: 0;
  transition: background 0.2s ease, transform 0.2s ease;
}
.carousel-dot.active {
  background: var(--brand-blue);
  transform: scale(1.3);
}

/* INFO COLUMN */
.combo-info-col {
  min-width: 0;
}
.combo-eyebrow {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--brand-blue);
  background: var(--brand-gold);
  padding: 3px 10px;
  border-radius: 20px;
  width: fit-content;
  margin-bottom: 10px;
}
.combo-name {
  font-size: 21px;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 10px;
  line-height: 1.3;
}
.included-names-row {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-bottom: 14px;
}
.included-name-chip {
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-soft);
  border: 1px solid var(--rule);
  padding: 4px 12px;
  border-radius: 20px;
}
.combo-price-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 6px;
}
.price-net {
  font-size: 24px;
  font-weight: 800;
  color: var(--brand-blue);
}
.price-mrp {
  font-size: 13px;
  color: var(--ink-faint);
  text-decoration: line-through;
}
.combo-facts-line {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-faint);
  letter-spacing: 0.2px;
}
.facts-dot {
  margin: 0 6px;
}

/* ACTION */
.action-btn-wrapper {
  align-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.brand-movement-add-btn {
  background: var(--brand-gold);
  border: none;
  color: var(--ink);
  border-radius: 50px;
  padding: 13px 26px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 6px 16px rgba(255, 199, 44, 0.28);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.brand-movement-add-btn:hover {
  background: var(--brand-blue);
  color: #fff;
  box-shadow: 0 6px 18px rgba(0, 61, 165, 0.3);
}
.brand-movement-add-btn.adding {
  opacity: 0.7;
  cursor: not-allowed;
}
.brand-movement-add-btn:disabled {
  cursor: not-allowed;
}

/* ADD-TO-CART TOAST — confirmation only, no quantity adjuster */
.combo-toast {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;
}
.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/* INCLUDED PRODUCTS — editorial, always visible, no accordion */
.included-section {
  border-top: 1px solid var(--rule);
  padding: 26px 32px 32px;
}
.included-eyebrow {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--ink-faint);
  margin-bottom: 22px;
}
.included-eyebrow i {
  font-size: 15px;
  color: var(--brand-blue);
}

.included-list {
  display: flex;
  gap: 40px;
}
.included-item {
  flex: 1;
  min-width: 0;
  padding-left: 24px;
  border-left: 1px solid var(--rule);
}
.included-item:first-child {
  padding-left: 0;
  border-left: none;
}

.included-item-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}
.included-item-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--ink);
}
.included-item-brand {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--brand-blue);
  white-space: nowrap;
}
.included-item-qty {
  display: block;
  font-size: 12px;
  color: var(--ink-faint);
  margin-top: 2px;
  margin-bottom: 14px;
}

/* EXPAND BLOCK — story preview always shown, Read more reveals the
   full story + facts + address together */
.included-item-expand-block {
  display: flex;
  flex-direction: column;
}
.included-item-story {
  font-size: 13.5px;
  line-height: 1.75;
  color: var(--ink-soft);
  margin-bottom: 6px;
}
.included-item-story.clamped {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.included-item-extra {
  margin-top: 8px;
  margin-bottom: 6px;
}

.story-toggle {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  align-self: flex-start;
  margin-top: 4px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: var(--brand-blue);
  transition: opacity 0.2s ease;
}
.story-toggle:hover {
  opacity: 0.72;
}
.story-toggle i {
  font-size: 15px;
  line-height: 1;
}

.included-item-facts {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 12.5px;
  color: var(--ink-soft);
  margin-bottom: 10px;
}
.included-item-facts span {
  display: flex;
  align-items: flex-start;
  gap: 7px;
}
.included-item-facts i {
  font-size: 14px;
  color: var(--brand-blue);
  margin-top: 1px;
  flex-shrink: 0;
}
.included-item-address {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  font-size: 12px;
  color: var(--ink-faint);
  line-height: 1.6;
  border-top: 1px dashed var(--rule);
  padding-top: 10px;
}
.included-item-address i {
  font-size: 13px;
  margin-top: 2px;
  flex-shrink: 0;
}

/* RESPONSIVE */
@media (max-width: 860px) {
  .combo-summary {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
    padding: 26px 22px;
  }
  .combo-info-col {
    width: 100%;
  }
  .combo-eyebrow {
    margin-inline: auto;
  }
  .included-names-row,
  .combo-price-row,
  .combo-facts-line {
    justify-content: center;
  }
  .action-btn-wrapper {
    width: 100%;
  }
  .brand-movement-add-btn {
    width: 100%;
  }
  .included-section {
    padding: 22px;
  }
  .included-list {
    flex-direction: column;
    gap: 24px;
  }
  .included-item {
    padding-left: 0;
    padding-top: 24px;
    border-left: none;
    border-top: 1px solid var(--rule);
  }
  .included-item:first-child {
    padding-top: 0;
    border-top: none;
  }
}
</style>
